import React, { SetStateAction, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Flex,
	IconButton,
	Image,
	Link as ChakraLink,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Spinner,
	Text,
} from '@chakra-ui/react';

import { MoreIcon } from 'shared/icons';
import { getFilenameFromURL } from 'shared/utils/get-filename-from-url';

import { AssetInfo } from '../../../../../widgets/hero-update-modal';
import { DragAndDropWrapper } from '../drag-and-drop-wrapper';
import { useAssetCardHairByIdQuery } from './queries.gen';

interface AssetCardHairProps {
	assets: { id: string; name: string }[];
	assetId: string;
	// openDetailsModal: () => void;
	handleSetFieldValue: (
		field: string,
		value: any,
		shouldValidate?: any,
	) => void;
	setCurrentAssetInfo?: React.Dispatch<SetStateAction<AssetInfo>>;
	isActive?: boolean;
	handlePhotoDownload: (imageLink: string, imageName: any) => void;
}

export const AssetCardHair: React.FC<AssetCardHairProps> = ({
	assets,
	assetId,
	// openDetailsModal,
	setCurrentAssetInfo,
	handleSetFieldValue,
	isActive,
	handlePhotoDownload,
}) => {
	const { data, loading } = useAssetCardHairByIdQuery({
		variables: {
			id: assetId,
		},
	});

	const handleAssetDelete = useCallback(() => {
		const newAssets = assets.filter((asset) => asset.id !== assetId);

		handleSetFieldValue(
			'hairs',
			assets.filter((asset) => asset.id !== assetId),
		);
		if (setCurrentAssetInfo) {
			setCurrentAssetInfo((prev) => ({
				...prev,
				hairsId: newAssets[0]?.id || '',
			}));
		}
	}, [assetId, assets, handleSetFieldValue, setCurrentAssetInfo]);

	const imageName = getFilenameFromURL(data?.hair.frontLink || '');
	const handleDownloadAsset = useCallback(() => {
		if (data) {
			handlePhotoDownload(data?.hair.frontLink || '', imageName);
		}
	}, [data, handlePhotoDownload, imageName]);

	return (
		<DragAndDropWrapper assetId={assetId}>
			<Flex flexDirection="column" gap={2} w="240px" maxW="240px">
				<Box
					h="min-content"
					userSelect="none"
					border="4px solid transparent"
					borderRadius="10px"
					position="relative"
				>
					<Flex
						height="180px"
						alignItems="center"
						justifyContent="center"
						border="1px solid #EEEEF2"
						borderRadius="10px"
						overflow="hidden"
						borderColor={isActive ? '#644DED' : ''}
						_hover={{
							borderColor: '#644DED',
							cursor: 'pointer',
						}}
					>
						{loading ? (
							<Spinner size="md" />
						) : (
							<Box
								position="relative"
								top="50%"
								transform="scale(0.9)"
								onClick={() =>
									setCurrentAssetInfo
										? setCurrentAssetInfo((prev: AssetInfo) => ({
												...prev,
												hairsId: assetId,
										  }))
										: null
								}
							>
								<Image
									transform="scale(0.85)"
									alt={data?.hair.name || ''}
									src={data?.hair.frontLink || ''}
								/>
							</Box>
						)}
					</Flex>

					{!loading && (
						<Box position="absolute" top={1} right={1}>
							<Menu placement="bottom-start">
								<MenuButton
									as={IconButton}
									aria-label="Options"
									icon={<MoreIcon w={5} h={5} />}
									variant="ghost"
								/>
								<MenuList rootProps={{ transform: 'scale(0)' }}>
									<ChakraLink
										as={Link}
										to={`/art-catalog/?id=${assetId}&name=${data?.hair.name}&link=${data?.hair.frontLink}&type=hair`}
										target="_blank"
										_hover={{ textDecoration: 'none' }}
										_focus={{ outline: 'none' }}
									>
										<MenuItem>Open in Art Catalog</MenuItem>
									</ChakraLink>
									<MenuItem onClick={handleAssetDelete}>Unlink Asset</MenuItem>
									<MenuItem onClick={handleDownloadAsset}>
										Download Asset
									</MenuItem>
								</MenuList>
							</Menu>
						</Box>
					)}
				</Box>
				<Text p={1} fontSize="12px" fontWeight={600} lineHeight="16px">
					{data?.hair.name || ''}
				</Text>
			</Flex>
		</DragAndDropWrapper>
	);
};
